import { get, getBlob, post, patch, del } from "./api";
import { getCurrentUserToken } from "./auth";
import type {
  ContactListPage,
  UnifiedContactResponse,
  PatchContactRequest,
  BulkOutreachRequest,
  BulkOutreachResult,
  LinkableContactResponse,
} from "../types/crm";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:5206";
const ACTIVE_ORG_STORAGE_KEY = "activeOrganizationId";

export interface ContactFilterParams {
  search?: string;
  divisions?: string[];
  states?: string[];
  contactType?: string;
  projectedDivisions?: string[];
  /** API `PipelineStage` values (e.g. "NoInterest"), not display labels. Players only. */
  pipelineStages?: string[];
}

interface GetContactsParams extends ContactFilterParams {
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Serializes the filters shared by the contacts list and its CSV export, so the exported
 * file can never cover a different set of contacts than the list on screen.
 */
const buildContactFilterQuery = (params: ContactFilterParams): URLSearchParams => {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  if (params.divisions && params.divisions.length > 0) query.set("division", params.divisions.join(","));
  if (params.states && params.states.length > 0) query.set("state", params.states.join(","));
  if (params.contactType) query.set("contactType", params.contactType);
  if (params.projectedDivisions && params.projectedDivisions.length > 0)
    query.set("projectedDivisions", params.projectedDivisions.join(","));
  if (params.pipelineStages && params.pipelineStages.length > 0)
    query.set("pipelineStages", params.pipelineStages.join(","));
  return query;
};

export const getContacts = (params: GetContactsParams): Promise<ContactListPage> => {
  const query = buildContactFilterQuery(params);
  query.set("pageNumber", String(params.pageNumber ?? 1));
  query.set("pageSize", String(params.pageSize ?? 15));
  return get(`/api/crm/contacts?${query.toString()}`);
};

/**
 * Downloads every contact matching the current filters as a CSV email list.
 * Contacts without an email address are omitted by the API.
 */
export const exportContactEmailList = (params: ContactFilterParams): Promise<Blob> =>
  getBlob(`/api/crm/contacts/export?${buildContactFilterQuery(params).toString()}`);

export const getContactById = (id: string): Promise<UnifiedContactResponse> =>
  get(`/api/crm/contacts/${id}`);

export const createCrmContact = (request: Record<string, unknown>): Promise<UnifiedContactResponse> =>
  post("/api/crm/contacts", request);

export interface SendContactMessageRequest {
  channel: string;
  body: string;
  subject?: string;
  inReplyToMessageId?: string;
  crmMediaId?: string;
}

export interface SendContactMessageResponse {
  success: boolean;
  error?: string;
  messageId?: string;
}

export const sendContactMessage = (
  contactType: string,
  contactId: string,
  request: SendContactMessageRequest
): Promise<SendContactMessageResponse> =>
  post(`/api/crm/contacts/${contactType}/${contactId}/messages`, request);

export interface SendContactMessageAttachmentRequest {
  channel: string;
  body: string;
  file: File;
}

export const sendContactMessageAttachment = async (
  contactType: string,
  contactId: string,
  request: SendContactMessageAttachmentRequest
): Promise<SendContactMessageResponse> => {
  const token = await getCurrentUserToken();
  const formData = new FormData();
  formData.append("channel", request.channel);
  formData.append("body", request.body);
  formData.append("file", request.file);

  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const orgOverrideId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  if (orgOverrideId) headers["X-Organization-Id"] = orgOverrideId;

  const response = await fetch(
    `${API_BASE_URL}/api/crm/contacts/${contactType}/${contactId}/messages/attachment`,
    { method: "POST", headers, body: formData }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { message?: string }).message || `Upload failed: ${response.status}`
    );
  }

  return response.json();
};

export const bulkOutreach = (request: BulkOutreachRequest): Promise<BulkOutreachResult> =>
  post("/api/crm/contacts/bulk-outreach", request);

export const updateCrmContact = (
  contactType: string,
  contactId: string,
  request: PatchContactRequest
): Promise<UnifiedContactResponse> =>
  patch(`/api/crm/contacts/${contactType}/${contactId}`, request);

export const deleteCrmContact = (contactType: string, contactId: string): Promise<void> =>
  del(`/api/crm/contacts/${contactType}/${contactId}`);

export const getLinkableContacts = (
  contactType: string,
  contactId: string
): Promise<LinkableContactResponse[]> =>
  get(`/api/crm/contacts/${contactType}/${contactId}/linkable-contacts`);

export interface IncludeParticipantRequest {
  participantContactId: string;
  participantContactType: string;
  channel: string;
}

export interface IncludeParticipantResponse {
  success: boolean;
  error?: string;
}

export const includeParticipant = (
  contactType: string,
  contactId: string,
  request: IncludeParticipantRequest
): Promise<IncludeParticipantResponse> =>
  post(`/api/crm/contacts/${contactType}/${contactId}/include-participant`, request);

export const linkCoachToTeam = (coachId: string, teamId: string | null): Promise<unknown> =>
  patch(`/api/coaches/${coachId}/team`, { teamId });

export const attachMessageToPlayer = (
  messageId: string,
  crmProspectProfileId: string
): Promise<{ message: string }> =>
  post(`/api/crm/messages/${messageId}/attach-player`, { crmProspectProfileId });

export const detachMessageFromPlayer = (messageId: string): Promise<{ message: string }> =>
  post(`/api/crm/messages/${messageId}/detach-player`, {});

export const getPlayerInteractions = (
  profileId: string,
  pageNumber = 1,
  pageSize = 50
): Promise<import("../types/crm").PlayerInteractionPage> =>
  get(`/api/crm/prospects/${profileId}/interactions?pageNumber=${pageNumber}&pageSize=${pageSize}`);
